<template>
  <cf-validated-form
    name="createGroup"
    @valid-submit="createGroup"
  >
    <div class="cf-flex cf-flex--column cf-flex--gap-16 cf-padding-bottom-16">
      <div
        v-if="order.groupOrder.token"
        :class="{ 'animation-slide-left': attemptedToCreateGroup }"
      >
        <div class="cf-flex cf-flex--column cf-flex--gap-16">
          <h2 class="cf-title cf-title--2">
            Invite Your Friends
          </h2>

          <p class="cf-subtitle cf-subtitle--2">
            Share this group link with your friends:
          </p>

          <cf-copy-to-clipboard
            name="groupOrderLink"
            label="Group Order Link"
            :value="order.groupOrder.link"
          />

          <button
            class="cf-button cf-button--primary cf-button--lg cf-button--fill"
            type="button"
            @click="browseMenu"
          >
            Browse Menu
          </button>

          <button
            class="cf-button cf-button--secondary cf-button--lg cf-button--fill"
            type="button"
            @click="cancelOrder"
          >
            Cancel Order
          </button>
        </div>
      </div>

      <div
        v-else
        class="cf-flex cf-flex--column cf-flex--gap-16"
      >
        <img
          class="cf-illustration cf-illustration--group-order"
          src="/images/illustrations/group-order-people.svg"
          role="presentation"
          alt=""
        >

        <h2 class="cf-title cf-title--2">
          Create a Group Order
        </h2>

        <p class="cf-subtitle cf-subtitle--2">
          Share a cart with guests. As the host, you can checkout once everyone is ready.
        </p>

        <template v-if="currentUser.accountId">
          <cf-selected-location-order-details />

          <p class="cf-subtitle cf-subtitle--3 cf-text-align-left">
            You won’t be able to change these details once the group order is created.
          </p>

          <cf-text-input
            v-model="hostName"
            type="text"
            label="Your Name"
            placeholder=""
            name="hostName"
            icon-class="fa-user"
            :validations="{
              max: 20,
              required: true
            }"
          />

          <button
            :class="['cf-button cf-button--primary cf-button--lg cf-button--fill', { 'cf-button--loading': creatingGroup }]"
            type="submit"
          >
            Create Group Order
          </button>

          <button
            class="cf-button cf-button--secondary cf-button--lg cf-button--fill"
            type="button"
            @click="$closeModal('editGroupOrder')"
          >
            Cancel
          </button>
        </template>

        <template v-else>
          <button
            class="cf-button cf-button--primary cf-button--lg cf-button--fill"
            type="button"
            @click="openSignInModalHandler"
          >
            Sign In
          </button>

          <cf-title-separator />

          <button
            class="cf-button cf-button--secondary cf-button--lg cf-button--fill"
            type="button"
            @click="openSignUpModalHandler"
          >
            Create Account
          </button>
        </template>
      </div>
    </div>
  </cf-validated-form>
</template>


<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';


  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';
  import CfTitleSeparator from '@/view/components/shared/cf-title-separator/index.vue';
  import CfCopyToClipboard from '@/view/components/shared/cf-copy-to-clipboard/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';

  import CfSelectedLocationOrderDetails from '@/view/components/globals/cf-selected-location-order-details/index.vue';


  export default {
    name: 'EditGroupOrder',

    components: {
      CfValidatedForm,
      CfSelectedLocationOrderDetails,
      CfTitleSeparator,
      CfCopyToClipboard,
      CfTextInput
    },

    data() {
      return {
        attemptedToCreateGroup: false,
        creatingGroup: false,
        hostName: null
      };
    },

    computed: {
      ...mapGetters('orders', ['order']),
      ...mapGetters('session', ['currentUser'])
    },

    watch: {
      'currentUser.accountId': 'autofillDetails'
    },

    created() {
      this.createdHandler();
    },

    methods: {
      autofillDetails() {
        if (this.currentUser.accountId) {
          this.hostName = this.currentUser.firstName;
        }
      },

      browseMenu() {
        this.$closeModal('editGroupOrder');

        this.$router.push({
          name: 'orderMenu',
          params: {
            merchantId: this.$route.params.merchantId
          }
        });
      },

      cancelOrder() {
        this.$openModal('confirmation', {
          ctaConfirmText: 'Yes, Cancel',
          primaryText: 'All items and guests will be removed from your cart.',
          onConfirm: () => this.$store.dispatch('orders/cancelOrder'),
          onConfirmSuccess: () => {
            // this.$triggerGoogleTrackingEvent('CancelGroupOrder', 'GroupOrder');

            this.$triggerNewRelicEvent('CancelGroupOrder', 'Information', 'CancelGroupOrder');

            this.$closeAllModals();
          },
          title: 'Cancel group order?'
        });
      },

      createdHandler() {
        this.autofillDetails();
      },

      async createGroup() {
        try {
          this.attemptedToCreateGroup = true;
          this.creatingGroup = true;

          await this.$store.dispatch('orders/groupOrderCreate', {
            hostName: this.hostName
          });

          // this.$triggerGoogleTrackingEvent('CreateGroupOrderSuccess', 'GroupOrder');

          this.$triggerNewRelicEvent('CreateGroupOrderSuccess', 'Information', 'Create Group Order Success');
        }

        catch (error) {
          errorHandlers.generic(error);

          // this.$triggerGoogleTrackingEvent('CreateGroupOrderFailure', 'GroupOrder');

          this.$triggerNewRelicEvent('CreateGroupOrderFailure', 'Error', 'Create Group Order Failure');
        }

        finally {
          this.creatingGroup = false;
        }
      },

      openSignInModalHandler() {
        this.$openModal('signIn');
      },

      openSignUpModalHandler() {
        this.$openModal('createAccount');
      }
    }
  };
</script>
